import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  HostBinding,
  Input,
  OnInit,
} from '@angular/core';
import { AppConfigService } from '@core/app-config';
import { DealsService } from '@core/deals';
import { PersonalizedContentService } from '@core/personalized-content/personalized-content.service';
import {
  Alignment,
  ButtonStyle,
  CLButtonStyles,
  NewWindow,
  PromoBlockView,
} from '@feature/types/promo-block.type';
import { DealLegalTextFormatterPipe } from '@shared/ui/pipes/dealLegalFormatterPipe/deal-legal-text-formatter.pipe';
import { BookerFacade } from '@store/booker/booker.facade';
import { OriginsFacade } from '@store/origins/origins.facade';
import { JbThemeEnum } from 'jb-component-library';
import { v4 as uuidV4 } from 'uuid';

import { PromoBlock } from './promo-block';

@Component({
  selector: 'dot-promo-block',
  templateUrl: './promo-block.component.html',
  styleUrls: ['./promo-block.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PromoBlockComponent implements OnInit, AfterViewInit {
  @Input() data: PromoBlockView[];
  enableLazyLoadImage: boolean;

  /** fullbleed only applicable when blocks contains ONE block only. */
  @HostBinding('class.fullbleed') fullbleed = false;

  /** Promo blocks to be displayed */
  blocks: PromoBlock[] = [];

  // shared properties across promo blocks
  promoTheme = JbThemeEnum;
  buttonStyle = ButtonStyle;
  clButtonStyles = CLButtonStyles;
  alignment = Alignment;
  newWindow = NewWindow;
  hasImageHeightWidthAttr = false;

  constructor(
    private personalizedContentService: PersonalizedContentService,
    private dealsService: DealsService,
    private originsFacade: OriginsFacade,
    private bookerFacade: BookerFacade,
    private appConfigService: AppConfigService,
    private cdr: ChangeDetectorRef,
    private dealLegalTextFormatterPipe: DealLegalTextFormatterPipe,
  ) {
    this.hasImageHeightWidthAttr = this.appConfigService.imageHeightWidthAttr;
  }

  ngOnInit(): void {
    this.enableLazyLoadImage = this.appConfigService.lazyLoadImages;
    this.blocks = this.data?.map(
      data =>
        new PromoBlock(
          this.personalizedContentService,
          this.dealsService,
          this.originsFacade,
          this.bookerFacade,
          this.appConfigService,
          data,
          this.dealLegalTextFormatterPipe,
        ),
    );

    if (this.blocks?.length === 1) {
      this.fullbleed = this.blocks[0].fullBleed;
    }
  }

  get randomID() {
    // this is required in case no fsPromoID is provided to prevent an undefined id and avoid conflicts with jtp-sdk library
    return uuidV4();
  }

  get isThreeUp(): boolean {
    return this.blocks?.length === 3;
  }

  get isTwoUp(): boolean {
    return this.blocks?.length === 2;
  }

  get isOneUp(): boolean {
    return this.blocks?.length === 1;
  }

  // this is specifically to resolve the issue that happens when the promo block
  // is loaded and images are async, you see a long extended promo block
  // DOT-8924 - this is a simple fix and seems to work really well, and shouldn't impact
  // performance too negatively.
  ngAfterViewInit(): void {
    this.cdr.detectChanges();
  }
}
